import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { restaurantGroupAPI, restaurantAPI, authAPI, stripeAPI, pricingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Loader2, Building2, Store, CreditCard, Check, Calendar, Shield, AlertCircle, X, CheckCircle, Tag } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Initialize Stripe lazily — only when Onboarding renders
let stripePromise = null;
function getStripePromise() {
  if (!stripePromise && process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
}

// Card Element styling
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1e293b',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': {
        color: '#94a3b8',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

// Step 2: Payment Form Component
function PaymentForm({ planType, billingCycle, onSuccess, onBack, onCouponApplied }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState('');

  // 100% discount → card not required.
  // Defensive: guard against undefined/null discounted_price
  const dp = couponResult != null ? Number(couponResult.discounted_price) : -1;
  const isFree = dp === 0;

  // Safety net: if something hangs and loading stays true > 35s, reset it
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => {
      setLoading(false);
      toast.error('Request timed out. Please check your connection and try again.');
    }, 35000);
    return () => clearTimeout(timer);
  }, [loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ── 100% free coupon path ─────────────────────────────────────────────────
    if (isFree) {
      setLoading(true);
      try {
        const { data: subscriptionData } = await stripeAPI.createFreeSubscription({
          coupon_code: couponResult.coupon_code,
          plan_type: planType,
          billing_cycle: billingCycle,
        });
        toast.success('Free access activated! Your 30-day trial has started.');
        onSuccess(subscriptionData);
      } catch (error) {
        toast.error(error.response?.data?.detail || error.userMessage || error.message || 'Failed to activate free access');
      } finally {
        setLoading(false);
      }
      return;
    }

    // ── Normal paid path ──────────────────────────────────────────────────────
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      // Step 1: Create setup intent
      const { data: setupData } = await stripeAPI.createSetupIntent({
        plan_type: planType,
        billing_cycle: billingCycle,
      });

      // Step 2: Confirm card setup — with timeout to unblock mobile hangs
      const confirmTimeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Card confirmation timed out. Please check your connection and try again.')), 25000)
      );
      const { error: setupError, setupIntent } = await Promise.race([
        stripe.confirmCardSetup(setupData.client_secret, {
          payment_method: { card: elements.getElement(CardElement) },
        }),
        confirmTimeout,
      ]);

      if (setupError) {
        toast.error(setupError.message);
        setLoading(false);
        return;
      }

      // Step 3: Create subscription with trial
      const { data: subscriptionData } = await stripeAPI.createSubscription({
        payment_method_id: setupIntent.payment_method,
        plan_type: planType,
        billing_cycle: billingCycle,
        ...(couponResult ? { coupon_code: couponResult.coupon_code } : {}),
      });

      toast.success('Payment method saved! Your 30-day trial has started.');
      onSuccess(subscriptionData);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.userMessage || error.message || 'Failed to setup payment');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const { data } = await stripeAPI.validateCoupon({
        coupon_code: couponInput.trim(),
        plan_type: planType,
        billing_cycle: billingCycle,
      });
      setCouponResult(data);
      onCouponApplied(data);
    } catch (error) {
      setCouponError(error.response?.data?.detail || 'Invalid or expired promo code');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponResult(null);
    setCouponInput('');
    setCouponError('');
    onCouponApplied(null);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Card section — hidden when 100% discount applied */}
      {!isFree ? (
        <div className="space-y-2">
          <Label>Card Details</Label>
          <div className="p-4 border border-slate-200 rounded-lg bg-white">
            <CardElement
              options={CARD_ELEMENT_OPTIONS}
              onChange={(e) => setCardComplete(e.complete)}
            />
          </div>
          <p className="text-xs text-slate-500">
            Your card will NOT be charged during the 30-day trial
          </p>
        </div>
      ) : (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="free-access-banner">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
            <div>
              <p className="font-medium text-green-800">No payment required</p>
              <p className="text-sm text-green-700 mt-0.5">
                Your coupon covers 100% of the cost. No credit card needed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Promo Code Section */}
      <div data-testid="promo-code-section">
        {!showCoupon ? (
          <button
            type="button"
            data-testid="show-coupon-btn"
            onClick={() => setShowCoupon(true)}
            className="flex items-center gap-1.5 text-sm text-orange-500 hover:text-orange-600 font-medium transition-colors"
          >
            <Tag size={13} />
            Have a promo code?
          </button>
        ) : couponResult ? (
          <div
            className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
            data-testid="coupon-applied-banner"
          >
            <div className="flex items-center gap-2">
              <CheckCircle size={15} className="text-green-600 flex-shrink-0" />
              <span className="text-sm font-medium text-green-800">
                {couponResult.coupon_code} &mdash; <strong>{couponResult.discount_display}</strong>
              </span>
            </div>
            <button
              type="button"
              onClick={removeCoupon}
              data-testid="remove-coupon-btn"
              className="text-slate-400 hover:text-red-500 transition-colors ml-2 flex-shrink-0"
            >
              <X size={15} />
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <Label className="text-sm text-slate-600">Promo Code (optional)</Label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Enter promo code"
                value={couponInput}
                onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleApplyCoupon())}
                className="flex-1 h-10 px-3 py-2 rounded-md border border-slate-200 bg-white text-sm uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder:normal-case placeholder:tracking-normal"
                data-testid="coupon-input"
                maxLength={50}
                autoComplete="off"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleApplyCoupon}
                disabled={couponLoading || !couponInput.trim()}
                data-testid="apply-coupon-btn"
                className="shrink-0 px-4"
              >
                {couponLoading ? <Loader2 size={15} className="animate-spin" /> : 'Apply'}
              </Button>
            </div>
            {couponError && (
              <p className="text-xs text-red-600 flex items-center gap-1" data-testid="coupon-error">
                <AlertCircle size={12} className="flex-shrink-0" />
                {couponError}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Security box — only when card is required */}
      {!isFree && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="text-green-600 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-green-800">Secure & Protected</p>
              <p className="text-sm text-green-700 mt-1">
                Your payment information is encrypted and secured by Stripe. 
                You can cancel anytime before your trial ends.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onBack}
          disabled={loading}
        >
          Back
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-orange-500 hover:bg-orange-600"
          disabled={(!isFree && (!stripe || !cardComplete)) || loading}
          data-testid="payment-submit-btn"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              {isFree ? <Check className="mr-2 h-4 w-4" /> : <CreditCard className="mr-2 h-4 w-4" />}
              Start Free Trial
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [brandName, setBrandName] = useState('');
  const [planType, setPlanType] = useState('single');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [locationName, setLocationName] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [pricingData, setPricingData] = useState(null);

  // Fetch pricing from backend (single source of truth)
  useEffect(() => {
    pricingAPI.getPricing()
      .then(({ data }) => setPricingData(data))
      .catch(() => {
        // Use fallback pricing if API fails
        setPricingData({
          plans: {
            single: { monthly_price: 59, yearly_price: 348, yearly_monthly_equivalent: 29 },
            multi: { monthly_price: 119, yearly_price: 708, yearly_monthly_equivalent: 59 },
          }
        });
      });
  }, []);

  // If user already has a restaurant group, skip to payment step
  useEffect(() => {
    restaurantGroupAPI.getMine()
      .then(() => setStep(2))
      .catch(() => {}); // No group yet — stay on step 1
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [loading, setLoading] = useState(false);
  const { updateUser } = useAuth();
  const navigate = useNavigate();

  // Calculate prices from backend pricing
  const getPlanPrices = (plan) => {
    if (!pricingData?.plans?.[plan]) {
      // Fallback prices
      return plan === 'single'
        ? { monthly: 59, yearly: 348, yearlySavings: 360 }
        : { monthly: 119, yearly: 708, yearlySavings: 720 };
    }
    const p = pricingData.plans[plan];
    const monthlyAnnualized = p.monthly_price * 12;
    return {
      monthly: p.monthly_price,
      yearly: p.yearly_price,
      yearlySavings: monthlyAnnualized - p.yearly_price,
    };
  };

  const plans = {
    single: getPlanPrices('single'),
    multi: getPlanPrices('multi'),
  };

  const currentPrice = plans[planType][billingCycle === 'yearly' ? 'yearly' : 'monthly'];
  const yearlySavings = plans[planType].yearlySavings;

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await restaurantGroupAPI.create({
        brand_name: brandName,
        plan_type: planType,
      });
      
      toast.success('Business created!');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create restaurant group');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    setStep(3);
  };

  const handleCreateRestaurant = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await restaurantAPI.create({ location_name: locationName });
      
      // Refresh token to get updated user data with restaurant_id
      const { data: refreshData } = await authAPI.refreshToken();
      localStorage.setItem('token', refreshData.token);
      localStorage.setItem('user', JSON.stringify(refreshData.user));
      updateUser(refreshData.user);
      
      toast.success('Setup complete! Welcome to ReStaff.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create restaurant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">RS</span>
            </div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope' }}>
              ReStaff
            </span>
          </div>
          
          {/* Progress indicator */}
          <div className="mt-6 flex items-center justify-center gap-2">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    step > s 
                      ? 'bg-green-500 text-white' 
                      : step === s 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {step > s ? <Check size={16} /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-12 h-1 rounded ${step > s ? 'bg-green-500' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="mt-2 text-sm text-slate-500">
            {step === 1 && 'Choose Your Plan'}
            {step === 2 && 'Payment Details'}
            {step === 3 && 'First Location'}
          </div>
        </div>

        {/* Step 1: Brand & Plan Selection */}
        {step === 1 && (
          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
                Set Up Your Brand
              </CardTitle>
              <CardDescription>
                Tell us about your business
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateGroup} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="brandName">Brand Name</Label>
                  <Input
                    id="brandName"
                    type="text"
                    placeholder="My Business"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    required
                    data-testid="onboarding-brand-input"
                  />
                  <p className="text-xs text-slate-500">
                    This is your business name that will appear across the app
                  </p>
                </div>

                <div className="space-y-4">
                  <Label>Choose Your Plan</Label>
                  <RadioGroup
                    value={planType}
                    onValueChange={setPlanType}
                    className="space-y-3"
                  >
                    <label
                      htmlFor="single"
                      className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-all ${
                        planType === 'single' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value="single" id="single" data-testid="plan-single-radio" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Store size={20} className="text-slate-600" />
                          <span className="font-medium">Single Location</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          1 location • Unlimited staff
                        </p>
                        <p className="text-lg font-bold text-slate-900 mt-2">
                          ${plans.single.monthly}/month
                        </p>
                      </div>
                    </label>

                    <label
                      htmlFor="multi"
                      className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-all ${
                        planType === 'multi' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value="multi" id="multi" data-testid="plan-multi-radio" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Building2 size={20} className="text-slate-600" />
                          <span className="font-medium">Multi Location</span>
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                            Popular
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          Unlimited locations • Unlimited staff
                        </p>
                        <p className="text-lg font-bold text-slate-900 mt-2">
                          ${plans.multi.monthly}/month
                        </p>
                      </div>
                    </label>
                  </RadioGroup>
                </div>

                {/* Billing Cycle Selection */}
                <div className="space-y-4">
                  <Label>Billing Cycle</Label>
                  <RadioGroup
                    value={billingCycle}
                    onValueChange={setBillingCycle}
                    className="grid grid-cols-2 gap-3"
                  >
                    <label
                      htmlFor="monthly"
                      className={`flex flex-col items-center p-4 border rounded-lg cursor-pointer transition-all ${
                        billingCycle === 'monthly' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value="monthly" id="monthly" className="sr-only" />
                      <Calendar size={24} className="text-slate-600 mb-2" />
                      <span className="font-medium">Monthly</span>
                      <span className="text-2xl font-bold text-slate-900 mt-1">
                        ${plans[planType].monthly}
                      </span>
                      <span className="text-xs text-slate-500">/month</span>
                    </label>

                    <label
                      htmlFor="yearly"
                      className={`flex flex-col items-center p-4 border rounded-lg cursor-pointer transition-all relative ${
                        billingCycle === 'yearly' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value="yearly" id="yearly" className="sr-only" />
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                        <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full">
                          Save ${yearlySavings}
                        </span>
                      </div>
                      <Calendar size={24} className="text-slate-600 mb-2" />
                      <span className="font-medium">Yearly</span>
                      <span className="text-2xl font-bold text-slate-900 mt-1">
                        ${plans[planType].yearly}
                      </span>
                      <span className="text-xs text-slate-500">/year</span>
                    </label>
                  </RadioGroup>
                </div>

                {/* Trial info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <CreditCard className="text-blue-600 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium text-blue-800">30-Day Free Trial</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Card required but you won&apos;t be charged until your trial ends. 
                        Cancel anytime before then.
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  disabled={loading || !brandName}
                  data-testid="onboarding-continue-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Continue to Payment'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Payment Details */}
        {step === 2 && (
          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
                Payment Details
              </CardTitle>
              <CardDescription>
                {appliedDiscount && Number(appliedDiscount.discounted_price) === 0
                  ? 'Your 100% coupon is applied — no card needed'
                  : 'Add your card to start your 30-day free trial'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Plan Summary */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-slate-900">
                      {planType === 'single' ? 'Single Location' : 'Multi Location'} Plan
                    </p>
                    <p className="text-sm text-slate-500">
                      Billed {billingCycle === 'yearly' ? 'annually' : 'monthly'}
                    </p>
                    {appliedDiscount && (
                      <span className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                        <Check size={10} /> {appliedDiscount.discount_display} applied
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    {appliedDiscount ? (
                      <>
                        <p className="text-sm text-slate-400 line-through">${currentPrice}</p>
                        <p className="text-2xl font-bold text-green-600" data-testid="discounted-price">
                          ${Number(appliedDiscount.discounted_price).toFixed(2)}
                        </p>
                      </>
                    ) : (
                      <p className="text-2xl font-bold text-slate-900" data-testid="plan-price">${currentPrice}</p>
                    )}
                    <p className="text-xs text-slate-500">after 30-day trial</p>
                  </div>
                </div>
              </div>

              <Elements stripe={getStripePromise()}>
                <PaymentForm
                  planType={planType}
                  billingCycle={billingCycle}
                  onSuccess={handlePaymentSuccess}
                  onBack={() => { setStep(1); setAppliedDiscount(null); }}
                  onCouponApplied={setAppliedDiscount}
                />
              </Elements>
            </CardContent>
          </Card>
        )}

        {/* Step 3: First Location */}
        {step === 3 && (
          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Check className="text-green-600" size={24} />
              </div>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
                Add Your First Location
              </CardTitle>
              <CardDescription>
                Your trial is active! Now set up your first location.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateRestaurant} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="locationName">Location Name</Label>
                  <Input
                    id="locationName"
                    type="text"
                    placeholder="Downtown Branch"
                    value={locationName}
                    onChange={(e) => setLocationName(e.target.value)}
                    required
                    data-testid="onboarding-location-input"
                  />
                  <p className="text-xs text-slate-500">
                    This helps identify the location
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  disabled={loading}
                  data-testid="onboarding-finish-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    'Complete Setup'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
